import { GridApi, RowNode, ValueGetterParams } from "ag-grid-community";
import lodashMemoize from "lodash/memoize";
import lodashGet from "lodash/get";
import { renameMetricColId } from "../column-formula/ag-grid-helper";
import { MetricsKind } from "../types";
import { metricsFormulas } from "../getter-setter-factory";
import { AllocationBagType, forEachAllocationBag } from "helpers/allocation-dimension";
import { Item, ItemFields } from "constants/product-collection";
import { ColumnTypes } from "components/product-collection/product-collection-views/product-collection-grid-view/product-collection-grid-view-helper";
import { safeParseFloat } from "lib/number";

export const getReferenceColId = lodashMemoize(
    (colId: string, referenceMetric: MetricsKind) => {
        return renameMetricColId(colId, referenceMetric);
    },
    (colId: string, referenceMetric: MetricsKind) => `${colId}_${referenceMetric}`
);

export const loopGridNode = (gridApi: GridApi, handler: (node: RowNode) => void) => {
    let isGroup = false;
    gridApi.forEachNodeAfterFilter((rowNode) => {
        // If row group is applied, take the value from the group to avoid recalculation at row level
        if (rowNode.group && !isGroup) {
            isGroup = true;
        }

        if (isGroup && !rowNode.group) {
            return;
        } else if (!isGroup && rowNode.group) {
            return;
        }

        handler(rowNode);
    });
};

export const referenceMetricValueOrCompute = (
    computeFn: () => number | null | undefined,
    valueGetterParams: ValueGetterParams,
    referenceMetric: MetricsKind,
    referenceRowId?: string,
    referenceColumnId?: string,
    disableSiblingOptimization?: boolean
) => {
    if (disableSiblingOptimization) {
        return computeFn();
    }

    const siblingColumnId = getReferenceColId(
        referenceColumnId || valueGetterParams.column.getColId(),
        referenceMetric
    );
    const siblingColumn = valueGetterParams.columnApi.getColumn(siblingColumnId);

    let value;
    if (!siblingColumn) {
        value = computeFn();
    } else {
        const rowNode = referenceRowId ? valueGetterParams.api.getRowNode(referenceRowId) : valueGetterParams.node;
        value = valueGetterParams.api.getValue(siblingColumn, rowNode);
    }

    return value;
};

export const sumColumnValues = lodashMemoize(
    (kind: MetricsKind, factoryOptions, getterParams) => {
        let colTotal = 0;
        loopGridNode(getterParams.api, (rowNode) => {
            const formulaFn = metricsFormulas[kind];
            const val = referenceMetricValueOrCompute(
                () =>
                    formulaFn({
                        factoryOptions,
                        getterParams: { ...getterParams, node: rowNode, data: rowNode.data },
                    }) as number | null | undefined,
                getterParams,
                kind,
                rowNode.id,
                getterParams.column.getColId()
            );
            colTotal += safeParseFloat(+val, 0);
        });

        return colTotal;
    },
    (kind, _factoryOptions, getterParams) => {
        return `${kind}_${getterParams.column.getColId()}_${getterParams.context.allocationBagRef?.revision}`;
    }
);

export const sumRowUnitsValues = lodashMemoize(
    (factoryOptions, getterParams) => {
        let colTotal = 0;
        if (!getterParams.context.allocationBagRef?.allocationBag) {
            return colTotal;
        }

        const itemId = lodashGet(getterParams.node.data, Item.FIELDS.ID);
        const { useSize, filteredDeliveriesForAssortmentSet, filteredDoorsForAssortmentSet } = factoryOptions;
        const size = lodashGet(getterParams, "data.size");
        if (useSize && size) {
            forEachAllocationBag(
                getterParams.context.allocationBagRef?.allocationBag,
                itemId,
                AllocationBagType.allocations,
                (dimensions, value) => {
                    if (dimensions.length > 1) {
                        // If there is a filter check to make sure this delivery or door is in that list
                        if (
                            filteredDeliveriesForAssortmentSet &&
                            !filteredDeliveriesForAssortmentSet.has(dimensions[0])
                        ) {
                            return;
                        }
                    }

                    if (filteredDoorsForAssortmentSet && !filteredDoorsForAssortmentSet.has(dimensions[1])) {
                        return;
                    }

                    if (size !== dimensions[2]) {
                        return;
                    }

                    colTotal += +value;
                }
            );
        } else {
            forEachAllocationBag(
                getterParams.context.allocationBagRef?.allocationBag,
                itemId,
                AllocationBagType.bulks,
                (dimensions, value) => {
                    if (dimensions.length > 1) {
                        // If there is a filter check to make sure this delivery or door is in that list
                        if (
                            filteredDeliveriesForAssortmentSet &&
                            !filteredDeliveriesForAssortmentSet.has(dimensions[0])
                        ) {
                            return;
                        }
                    }

                    if (
                        filteredDoorsForAssortmentSet &&
                        !filteredDoorsForAssortmentSet.has(dimensions[dimensions.length - 1])
                    ) {
                        return;
                    }

                    colTotal += +value;
                }
            );
        }

        return colTotal;
    },
    (_factoryOptions, getterParams) => {
        return `${getterParams.node.id}_${getterParams.column.getColId()}_${
            getterParams.context.allocationBagRef?.revision
        }`;
    }
);

export const sumRowTotalRetailValues = lodashMemoize(
    (factoryOptions, getterParams) => {
        let colTotal = 0;
        if (!getterParams.context.allocationBagRef?.allocationBag) {
            return colTotal;
        }

        const itemId = lodashGet(getterParams.node.data, Item.FIELDS.ID);
        const { useSize, filteredDeliveriesForAssortmentSet, filteredDoorsForAssortmentSet, rootGridApi } =
            factoryOptions;
        const size = lodashGet(getterParams, "data.size");
        const { data, node }: ValueGetterParams = getterParams;
        if (useSize && size) {
            forEachAllocationBag(
                getterParams.context.allocationBagRef?.allocationBag,
                itemId,
                AllocationBagType.allocations,
                (dimensions, value) => {
                    if (dimensions.length > 1) {
                        // If there is a filter check to make sure this delivery or door is in that list
                        if (
                            filteredDeliveriesForAssortmentSet &&
                            !filteredDeliveriesForAssortmentSet.has(dimensions[0])
                        ) {
                            return;
                        }
                    }

                    if (filteredDoorsForAssortmentSet && !filteredDoorsForAssortmentSet.has(dimensions[1])) {
                        return;
                    }

                    if (size !== dimensions[2]) {
                        return;
                    }

                    const door = filteredDoorsForAssortmentSet.get(dimensions[1]);
                    const retailFieldName = lodashGet(door, "retail.fieldName");
                    if (door && retailFieldName) {
                        let retail;
                        if (rootGridApi) {
                            retail = rootGridApi.getValue(retailFieldName, node);
                        }

                        if (!retail) {
                            retail = lodashGet(data, [ItemFields.Fields, retailFieldName, "string_value"]);
                        }

                        const doorRetail = safeParseFloat(retail, 0);
                        colTotal += +value * doorRetail;
                    }
                }
            );
        } else {
            forEachAllocationBag(
                getterParams.context.allocationBagRef?.allocationBag,
                itemId,
                AllocationBagType.bulks,
                (dimensions, value) => {
                    if (dimensions.length > 1) {
                        // If there is a filter check to make sure this delivery or door is in that list
                        if (
                            filteredDeliveriesForAssortmentSet &&
                            !filteredDeliveriesForAssortmentSet.has(dimensions[0])
                        ) {
                            return;
                        }
                    }

                    if (
                        filteredDoorsForAssortmentSet &&
                        !filteredDoorsForAssortmentSet.has(dimensions[dimensions.length - 1])
                    ) {
                        return;
                    }

                    const door = filteredDoorsForAssortmentSet.get(dimensions[dimensions.length - 1]);
                    const retailFieldName = lodashGet(door, "retail.fieldName");
                    if (door && retailFieldName) {
                        let retail = getterParams.api.getValue(retailFieldName, node);

                        if (!retail && rootGridApi) {
                            retail = rootGridApi.getValue(retailFieldName, node);
                        }

                        if (!retail) {
                            retail = lodashGet(data, [ItemFields.Fields, retailFieldName, "string_value"]);
                        }

                        const doorRetail = safeParseFloat(retail, 0);
                        colTotal += +value * doorRetail;
                    }
                }
            );
        }

        return colTotal;
    },
    (_factoryOptions, getterParams) => {
        return `${getterParams.node.id}_${ColumnTypes.AllDimensionTotal}_${getterParams.context.allocationBagRef?.revision}`;
    }
);
